import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalculatorInputs } from "./CalculatorInputs";
import { CalculatorResults } from "./CalculatorResults";

interface CalculatorInputs {
  ticketPrice: number;
  leadsPerMonth: number;
  profitMargin: number;
  closeRate: number;
}

export const LeadCalculatorTool = () => {
  const { toast } = useToast();
  const [inputs, setInputs] = useState<CalculatorInputs>({
    ticketPrice: 1500,
    leadsPerMonth: 12,
    profitMargin: 35,
    closeRate: 35,
  });

  const calculateResults = () => {
    const totalRevenue = (inputs.leadsPerMonth * (inputs.closeRate / 100) * inputs.ticketPrice);
    const totalProfit = totalRevenue * (inputs.profitMargin / 100);
    return { totalRevenue, totalProfit };
  };

  const { totalRevenue, totalProfit } = calculateResults();

  const handleInputChange = (field: keyof CalculatorInputs, value: number | string) => {
    let numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (field === 'ticketPrice' || field === 'leadsPerMonth') {
      numValue = Math.max(0, numValue);
    }
    setInputs(prev => ({ ...prev, [field]: numValue }));
  };

  const useExampleData = () => {
    setInputs({
      ticketPrice: 1500,
      leadsPerMonth: 12,
      profitMargin: 35,
      closeRate: 35,
    });
  };

  const chartData = [
    {
      name: 'Revenue & Profit',
      Revenue: totalRevenue,
      Profit: totalProfit,
    },
  ];

  const downloadResults = () => {
    const content = `
      Lead Value Calculator Results
      ---------------------------
      Average Ticket Price: $${inputs.ticketPrice}
      Leads Per Month: ${inputs.leadsPerMonth}
      Profit Margin: ${inputs.profitMargin}%
      Close Rate: ${inputs.closeRate}%
      
      Estimated Total Revenue: $${totalRevenue.toFixed(2)}
      Estimated Total Profit: $${totalProfit.toFixed(2)}
    `;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead-calculator-results.txt';
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Results Downloaded",
      description: "Your calculator results have been downloaded successfully.",
    });
  };

  const shareViaEmail = () => {
    const subject = "Lead Calculator Results - Home Pros OKC";
    const body = `
      Lead Value Calculator Results from Home Pros OKC
      
      Average Ticket Price: $${inputs.ticketPrice}
      Leads Per Month: ${inputs.leadsPerMonth}
      Profit Margin: ${inputs.profitMargin}%
      Close Rate: ${inputs.closeRate}%
      
      Estimated Total Revenue: $${totalRevenue.toFixed(2)}
      Estimated Total Profit: $${totalProfit.toFixed(2)}
      
      Calculate your own leads value at: https://homeprosokc.com/lead-calculator
    `;
    
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    toast({
      title: "Email Opened",
      description: "Your default email client has been opened with the results.",
    });
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto">
      <div className="text-center max-w-3xl mx-auto px-4">
        <h1 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-primary">Lead Value Calculator</h1>
        <p className="text-base sm:text-lg text-muted-foreground">
          Discover the value of your leads and how much additional revenue your business could generate with Home Pros OKC. 
          Use our calculator to estimate your potential revenue and profit.
        </p>
      </div>

      <Card className="border-primary/20 shadow-lg mx-4">
        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 p-4 sm:p-6">
          <CardTitle className="text-primary text-lg sm:text-xl">Calculate Your Lead Value</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 p-4 sm:p-8">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
            <CalculatorInputs 
              inputs={inputs}
              handleInputChange={handleInputChange}
              useExampleData={useExampleData}
            />
            <CalculatorResults 
              totalRevenue={totalRevenue}
              totalProfit={totalProfit}
              chartData={chartData}
              downloadResults={downloadResults}
              shareViaEmail={shareViaEmail}
            />
          </div>
        </CardContent>
      </Card>

      <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 sm:p-8 rounded-lg text-center space-y-4 mx-4">
        <h2 className="text-xl sm:text-2xl font-bold text-primary">Ready to grow your business?</h2>
        <p className="text-base sm:text-lg text-muted-foreground mb-4 sm:mb-6">
          List your services on Home Pros OKC today! Have questions? <Link to="/contact" className="text-primary hover:underline">Contact us</Link>.
        </p>
        <Link to="/for-pros">
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-6 sm:px-8 py-2 h-auto text-sm sm:text-base">
            Get Started Now
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default LeadCalculatorTool;